import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';

export const SUPPORTED_LANGS = ['en', 'ru', 'es', 'pt'] as const;
export type SupportedLang = typeof SUPPORTED_LANGS[number];
// import LanguageDetector from 'i18next-browser-languagedetector'; // For later

// Attempt to get Telegram language code
// This needs to be adapted based on how Telegram WebApp context is available
const getTelegramLanguage = (): SupportedLang | undefined => {
  // Check if running in a browser environment and if Telegram WebApp context exists
  if (typeof window !== 'undefined' && 
      window.Telegram && 
      window.Telegram.WebApp && 
      window.Telegram.WebApp.initDataUnsafe && 
      window.Telegram.WebApp.initDataUnsafe.user && 
      window.Telegram.WebApp.initDataUnsafe.user.language_code
  ) {
    const lang = window.Telegram.WebApp.initDataUnsafe.user.language_code;
    // Return the base language code (e.g., 'en' from 'en-US', 'ru' from 'ru-RU')
    return lang.split('-')[0] as SupportedLang;
  }
  // If context is not available or language code is missing, return undefined
  console.info('Telegram WebApp context or language code not found.');
  return undefined;
};

// Helper function to determine initial language
const getInitialLanguage = (): SupportedLang => {
  // 1. Check Telegram context first
  const telegramLang = getTelegramLanguage();
  if (telegramLang && SUPPORTED_LANGS.includes(telegramLang)) {
    console.info(`Using Telegram user language: ${telegramLang}`);
    return telegramLang;
  }

  // 2. Check localStorage
  const storedLang = localStorage.getItem('lang');
  if (storedLang && SUPPORTED_LANGS.includes(storedLang as SupportedLang)) {
    console.info(`Using stored language preference: ${storedLang}`);
    return storedLang as SupportedLang;
  }

  // 3. Fallback to English
  console.info('No language preference found, falling back to English');
  return 'en' as SupportedLang;
};


i18n
  .use(HttpApi) // Load translations using http (e.g., from public/locales)
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    // debug: true, // Uncomment to see logs
    fallbackLng: 'en',
    lng: getInitialLanguage(), // Set initial language
    interpolation: {
      escapeValue: false, // React already safes from xss
      format: function(value) {
        // Ensure numeric values are properly converted to strings
        if (typeof value === 'number') {
          return value.toString();
        }
        return value;
      },
      prefix: '{{',
      suffix: '}}'
    },
    backend: {
      loadPath: '/i18n/{{lng}}.json', // Path to translation files
    },
    // Default namespace (optional if you only have one file)
    // ns: ['translation'],
    // defaultNS: 'translation',
  });

// Function to change the language and persist the choice in localStorage
export const changeLanguage = (lng: SupportedLang) => {
  if (i18n.language !== lng && SUPPORTED_LANGS.includes(lng)) {
    i18n.changeLanguage(lng).then(() => {
      try {
        localStorage.setItem('lang', lng);
      } catch (e) {
        console.warn('Could not save language preference to localStorage:', e);
      }
    });
  }
};

export default i18n;

// It's common to re-export useTranslation for convenience in components
export { useTranslation } from 'react-i18next'; 